import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const StrongCTA = () => {
  const navigate = useNavigate();
  const { ref: contentRef, isVisible } = useScrollAnimation({ threshold: 0.2 });

  const handleQuoteClick = () => {
    navigate('/contact');
    setTimeout(() => {
      const form = document.getElementById('enquiry-form');
      if (form) {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Luxury gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
      
      {/* Animated gold accent lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-600 to-transparent opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-600 to-transparent opacity-50"></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div ref={contentRef} className="relative max-w-5xl mx-auto px-6 text-center">
        {/* Urgency badge */}
        <div className={`inline-flex items-center gap-2 px-5 py-2 bg-yellow-600/20 border border-yellow-600/30 rounded-full mb-8 animate-pulse scroll-animate ${isVisible ? 'visible' : ''}`}>
          <i className="ri-time-line text-yellow-500 text-lg w-5 h-5 flex items-center justify-center"></i>
          <span className="text-yellow-500 text-sm font-medium tracking-wide">Limited stock for peak weekends — Book early to secure your items</span>
        </div>

        {/* Main headline */}
        <h2 className={`text-5xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight scroll-animate ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '100ms' }}>
          Planning an Event?
        </h2>

        {/* Sub-headline */}
        <p className={`text-2xl md:text-3xl text-gray-300 font-light mb-12 tracking-wide scroll-animate ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '200ms' }}>
          Let's make it seamless.
        </p>

        {/* Supporting text */}
        <p className={`text-lg text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed scroll-animate ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '300ms' }}>
          From intimate gatherings to grand celebrations, we provide premium equipment and professional service that elevates every moment. Get your personalised quote in minutes.
        </p>

        {/* Primary CTA button */}
        <div className={`scroll-animate ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '400ms' }}>
          <button
            onClick={handleQuoteClick}
            className="group relative inline-flex items-center gap-3 px-12 py-5 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold text-lg rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-600/50 whitespace-nowrap cursor-pointer"
          >
            {/* Button glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <span className="relative z-10">Request a Quote Today</span>
            <i className="ri-arrow-right-line text-2xl relative z-10 group-hover:translate-x-1 transition-transform duration-300 w-6 h-6 flex items-center justify-center"></i>
          </button>
        </div>

        {/* Trust indicators */}
        <div className={`flex flex-wrap items-center justify-center gap-8 mt-16 text-gray-400 text-sm scroll-animate ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '500ms' }}>
          <div className="flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-yellow-600 text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Free Quote</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-time-fill text-yellow-600 text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Quick Response</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-shield-check-fill text-yellow-600 text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Fully Insured</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-truck-fill text-yellow-600 text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Reliable Delivery</span>
          </div>
        </div>

        {/* Secondary action */}
        <div className={`mt-12 scroll-animate ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '600ms' }}>
          <button
            onClick={() => navigate('/equipment-hire')}
            className="text-gray-400 hover:text-yellow-500 transition-colors duration-300 text-base font-medium group cursor-pointer whitespace-nowrap"
          >
            Or browse our full collection
            <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
          </button>
        </div>
      </div>

      {/* Decorative corner accents */}
      <div className="absolute top-8 left-8 w-24 h-24 border-l-2 border-t-2 border-yellow-600/30"></div>
      <div className="absolute bottom-8 right-8 w-24 h-24 border-r-2 border-b-2 border-yellow-600/30"></div>
    </section>
  );
};

export default StrongCTA;